
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, MessageCircle, Heart, X, MapPin, Calendar } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface RoommateProfile {
  id: string;
  full_name: string;
  avatar_url?: string;
  age?: number;
  university_id?: string;
  university_name?: string;
  budget_max?: number;
  roommates_count?: number;
  gender?: string;
  expected_move_in?: string;
  interests?: string[];
  bio?: string;
  compatibility_score?: number;
}

const RoommateMatching = () => {
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const [potentialRoommates, setPotentialRoommates] = useState<RoommateProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (user && profile?.role === 'student') {
      fetchPotentialRoommates();
    }
  }, [user, profile]);

  const fetchPotentialRoommates = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          avatar_url,
          budget_max,
          roommates_count,
          gender,
          expected_move_in
        `)
        .eq('role', 'student')
        .neq('id', user?.id)
        .limit(20);

      if (error) throw error;

      // Add compatibility scoring and filter compatible matches
      const compatibleRoommates = (data || []).map(roommate => ({
        ...roommate,
        full_name: roommate.full_name || 'Anonymous User',
        compatibility_score: calculateCompatibility(roommate),
        interests: ['Study', 'Sports', 'Music'], // Mock data
        bio: 'Looking for a quiet roommate to share expenses.'
      })).filter(r => (r.compatibility_score || 0) > 60);

      setPotentialRoommates(compatibleRoommates);
    } catch (error) {
      console.error('Error fetching roommates:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateCompatibility = (roommate: any) => {
    let score = 50; // Base score
    
    // Budget compatibility
    if (profile?.budget_max && roommate.budget_max) {
      const budgetDiff = Math.abs(profile.budget_max - roommate.budget_max);
      score += Math.max(0, 30 - (budgetDiff / 100));
    }
    
    // Gender preference
    if (profile?.gender === roommate.gender) {
      score += 20;
    }
    
    // Move-in date compatibility
    if (profile?.expected_move_in && roommate.expected_move_in) {
      const date1 = new Date(profile.expected_move_in);
      const date2 = new Date(roommate.expected_move_in);
      const timeDiff = Math.abs(date1.getTime() - date2.getTime());
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      if (daysDiff <= 30) score += 15;
    }
    
    return Math.min(100, score);
  };

  const handleSwipe = async (roommateId: string, action: 'like' | 'pass') => {
    try {
      // Store swipe action in localStorage for demo
      const swipes = JSON.parse(localStorage.getItem(`swipes_${user?.id}`) || '{}');
      swipes[roommateId] = action;
      localStorage.setItem(`swipes_${user?.id}`, JSON.stringify(swipes));

      if (action === 'like') {
        toast.success(t('roommate_liked', 'تم الإعجاب بالرفيق', 'Roommate liked!'));
      }

      // Move to next roommate
      setCurrentIndex(prev => prev + 1);
    } catch (error) {
      console.error('Error handling swipe:', error);
    }
  };

  const sendMessage = async (roommateId: string) => {
    toast.info(t('feature_coming_soon', 'هذه الميزة قادمة قريباً', 'Feature coming soon'));
  };

  if (!user || profile?.role !== 'student') {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">
            {t('student_only_feature', 'هذه الميزة متاحة للطلاب فقط', 'This feature is only available for students')}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentRoommate = potentialRoommates[currentIndex];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          {t('roommate_matching', 'مطابقة الرفقاء', 'Roommate Matching')}
        </CardTitle>
        <CardDescription>
          {t('find_compatible_roommates', 'ابحث عن رفقاء متوافقين معك', 'Find compatible roommates based on your preferences')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!currentRoommate ? (
          <div className="text-center py-8 space-y-4">
            <Users className="h-12 w-12 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground">
              {t('no_more_roommates', 'لا يوجد المزيد من الرفقاء المحتملين', 'No more potential roommates at the moment')}
            </p>
            <Button onClick={fetchPotentialRoommates}>
              {t('refresh', 'تحديث', 'Refresh')}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <Avatar className="h-24 w-24 mx-auto mb-4">
                <AvatarImage src={currentRoommate.avatar_url} />
                <AvatarFallback>
                  {currentRoommate.full_name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
              <h3 className="text-xl font-semibold">{currentRoommate.full_name}</h3>
              <div className="flex items-center justify-center gap-2 mt-2">
                <Badge variant="secondary">
                  {currentRoommate.compatibility_score}% {t('match', 'توافق', 'match')}
                </Badge>
                {currentRoommate.university_name && (
                  <Badge variant="outline">
                    <MapPin className="h-3 w-3 mr-1" />
                    {currentRoommate.university_name}
                  </Badge>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {currentRoommate.budget_max && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('budget', 'الميزانية', 'Budget')}:</span>
                  <span>{currentRoommate.budget_max} EGP</span>
                </div>
              )}
              {currentRoommate.expected_move_in && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('move_in_date', 'تاريخ الانتقال', 'Move-in Date')}:</span>
                  <span>{new Date(currentRoommate.expected_move_in).toLocaleDateString()}</span>
                </div>
              )}
              {currentRoommate.gender && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('gender', 'الجنس', 'Gender')}:</span>
                  <span>{currentRoommate.gender}</span>
                </div>
              )}
            </div>

            {currentRoommate.bio && (
              <div>
                <h4 className="font-medium mb-2">{t('about', 'نبذة', 'About')}</h4>
                <p className="text-sm text-muted-foreground">{currentRoommate.bio}</p>
              </div>
            )}

            {currentRoommate.interests && (
              <div>
                <h4 className="font-medium mb-2">{t('interests', 'الاهتمامات', 'Interests')}</h4>
                <div className="flex flex-wrap gap-2">
                  {currentRoommate.interests.map((interest, index) => (
                    <Badge key={index} variant="outline">{interest}</Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-center gap-4 pt-4">
              <Button
                variant="outline"
                size="lg"
                onClick={() => handleSwipe(currentRoommate.id, 'pass')}
                className="flex-1"
              >
                <X className="h-5 w-5 mr-2" />
                {t('pass', 'تمرير', 'Pass')}
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => sendMessage(currentRoommate.id)}
              >
                <MessageCircle className="h-5 w-5" />
              </Button>
              <Button
                size="lg"
                onClick={() => handleSwipe(currentRoommate.id, 'like')}
                className="flex-1 bg-pink-500 hover:bg-pink-600"
              >
                <Heart className="h-5 w-5 mr-2" />
                {t('like', 'إعجاب', 'Like')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RoommateMatching;
